/**
 * SWTORBIT platform data (world, items, flashpoints), via api-server's
 * `/api/platform` proxy.
 *
 * The browser never talks to the platform's API: api-server holds the key and
 * caches. Only *images* (maps, item icons, loading screens) load straight from
 * the platform's media URL, which is public and immutable.
 *
 * All calls use `credentials: 'include'` so the httpOnly auth cookie is sent.
 */

import { API_URL } from '../config';
import type {
  PlatformAreaMapsResponse,
  PlatformAreasResponse,
  PlatformFlashpointsResponse,
  PlatformItemFilterLookups,
  PlatformItemPage,
  PlatformItemSearchParams,
  PlatformItemSummary,
  PlatformLocateResult,
} from '@swtorbit/shared-socket-events';

const BASE = `${API_URL}/api/platform`;

async function getJson<T>(url: string, fallback: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { credentials: 'include', signal });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(
      data.error === 'platform_unavailable'
        ? 'The platform is unavailable. Try again shortly.'
        : data.error || fallback,
    );
  }
  return response.json() as Promise<T>;
}

/**
 * Open-world areas grouped by planet (the Content Library's area picker).
 * @endpoint GET /api/platform/areas
 */
export function fetchPlatformAreas(): Promise<PlatformAreasResponse> {
  return getJson(`${BASE}/areas`, 'Failed to load platform areas');
}

/**
 * Planet, world map, zone map and pixel positions for a combat-log position.
 * `world: null` = the position is outside every map of that area.
 * @endpoint GET /api/platform/locate
 */
export function locatePlatform(
  logAreaId: number,
  x: number,
  y: number,
  z?: number,
  signal?: AbortSignal,
): Promise<PlatformLocateResult> {
  const q = new URLSearchParams({ area: String(logAreaId), x: String(x), y: String(y) });
  if (z !== undefined) q.set('z', String(z));
  return getJson(`${BASE}/locate?${q}`, 'Failed to locate position', signal);
}

/**
 * An open-world area's world map and drawable zone maps (the location picker).
 * @endpoint GET /api/platform/areas/:logAreaId/maps
 */
export function fetchPlatformAreaMaps(logAreaId: number): Promise<PlatformAreaMapsResponse> {
  return getJson(`${BASE}/areas/${logAreaId}/maps`, 'Failed to load maps for this area');
}

/**
 * Item search, paged (at most 50 per page). The treasure editor's item picker.
 * @endpoint GET /api/platform/items
 */
export function searchPlatformItems(
  params: PlatformItemSearchParams,
  signal?: AbortSignal,
): Promise<PlatformItemPage> {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') q.set(key, String(value));
  }
  return getJson(`${BASE}/items?${q}`, 'Failed to search items', signal);
}

/**
 * The display qualities and Cartel rarities items can be filtered by.
 * @endpoint GET /api/platform/items/lookups
 */
export function fetchPlatformItemLookups(): Promise<PlatformItemFilterLookups> {
  return getJson(`${BASE}/items/lookups`, 'Failed to load item filters');
}

/**
 * One item's summary (name, icon, display quality, rarity).
 * @endpoint GET /api/platform/items/:id
 */
export function fetchPlatformItem(id: number, signal?: AbortSignal): Promise<PlatformItemSummary> {
  return getJson(`${BASE}/items/${id}`, 'Failed to load item', signal);
}

/**
 * Every pickable flashpoint (combat-log area id, name, loading screen).
 * @endpoint GET /api/platform/flashpoints
 */
export function fetchPlatformFlashpoints(): Promise<PlatformFlashpointsResponse> {
  return getJson(`${BASE}/flashpoints`, 'Failed to load flashpoints');
}
